import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table, Tag, Spin, Empty, Modal, message, Space, Badge, Tooltip, Typography, Row, Col, Card, Input,
} from "antd";
import {
  EyeOutlined, CheckCircleOutlined, ClockCircleOutlined,
  SearchOutlined, FileTextOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import ComplianceDocViewer from "../../../components/ComplianceDocViewer/ComplianceDocViewer";

const { Text } = Typography;

const PageWrapper = styled.div`
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 4px 0; }
  p.subtitle { color: #666; font-size: 14px; margin: 0 0 24px 0; }
`;

const StatCard = styled(Card)`
  border-radius: 8px;
  .stat-inner { display: flex; align-items: center; gap: 14px; }
  .stat-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
  }
  .stat-info h3 { margin: 0; font-size: 22px; font-weight: 700; color: #014c75; line-height: 1.2; }
  .stat-info p { margin: 0; font-size: 12px; color: #888; }
`;

const DocBadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const DOC_TYPES = ['nda', 'contract', 'declaration'];
const DOC_TYPE_LABELS = { nda: 'NDA', contract: 'Contract', declaration: 'Declaration' };
const DOC_TYPE_COLORS = { nda: 'blue', contract: 'purple', declaration: 'cyan' };

const CustomerComplianceDocs = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDoc, setViewDoc] = useState(null);
  const [search, setSearch] = useState("");

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(API_CONST.COMPLIANCE_DOCUMENTS_LIST, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load compliance documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Group flat doc list into one row per hiring record
  const groupedRows = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      const key = doc.hiringRecordId;
      if (!map.has(key)) {
        map.set(key, {
          hiringRecordId: doc.hiringRecordId,
          talentName: doc.talentName || doc.talent?.name || "—",
          partnerOrg: doc.partnerOrganization,
          jobId: doc.jobId || "—",
          docs: {},
          allSigned: false,
        });
      }
      map.get(key).docs[doc.docType] = doc;
    });
    // compute allSigned
    map.forEach((row) => {
      row.allSigned = DOC_TYPES.every(
        (t) => row.docs[t]?.status === "signed"
      );
    });
    return Array.from(map.values());
  }, [documents]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return groupedRows;
    const q = search.toLowerCase();
    return groupedRows.filter(
      (r) =>
        r.hiringRecordId?.toLowerCase().includes(q) ||
        r.talentName?.toLowerCase().includes(q)
    );
  }, [groupedRows, search]);

  const stats = useMemo(() => {
    const total = groupedRows.length;
    const allSigned = groupedRows.filter((r) => r.allSigned).length;
    const pending = total - allSigned;
    return { total, allSigned, pending };
  }, [groupedRows]);

  const handleViewDoc = async (doc) => {
    if (doc.status === "pending") {
      message.info("This document is still awaiting the partner's signature.");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.COMPLIANCE_DOCUMENTS}/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewDoc(res.data.document);
    } catch (err) {
      if (err.response?.status === 403) {
        message.info("This document is still awaiting the partner's signature.");
      } else {
        message.error("Failed to load document");
      }
    }
  };

  const columns = [
    {
      title: "Hiring ID",
      dataIndex: "hiringRecordId",
      key: "hiringRecordId",
      width: 160,
      render: (id) => <Text style={{ fontWeight: 500, color: "#014c75" }}>{id}</Text>,
    },
    {
      title: "Talent",
      dataIndex: "talentName",
      key: "talentName",
      width: 180,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 160,
      render: (id) => <Text style={{ color: "#014c75" }}>{id || "—"}</Text>,
    },
    {
      title: "NDA",
      key: "nda",
      width: 110,
      render: (_, row) => renderDocCell(row.docs["nda"]),
    },
    {
      title: "Contract",
      key: "contract",
      width: 120,
      render: (_, row) => renderDocCell(row.docs["contract"]),
    },
    {
      title: "Declaration",
      key: "declaration",
      width: 130,
      render: (_, row) => renderDocCell(row.docs["declaration"]),
    },
    {
      title: "Overall",
      key: "overall",
      width: 110,
      render: (_, row) =>
        row.allSigned ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>All Signed</Tag>
        ) : (
          <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>
        ),
    },
  ];

  const renderDocCell = (doc) => {
    if (!doc) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
    const isSigned = doc.status === "signed";
    return (
      <Space size={4}>
        {isSigned ? (
          <Tag
            color="success"
            icon={<CheckCircleOutlined />}
            style={{ cursor: "pointer" }}
            onClick={() => handleViewDoc(doc)}
          >
            Signed
          </Tag>
        ) : (
          <Tooltip title="Awaiting partner signature">
            <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>
          </Tooltip>
        )}
        {isSigned && (
          <Tooltip title="View document">
            <EyeOutlined
              style={{ color: "#1890ff", cursor: "pointer", fontSize: 15 }}
              onClick={() => handleViewDoc(doc)}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  return (
    <PageWrapper>
      <h1>Compliance Documents</h1>
      <p className="subtitle">
        View NDA, Contract, and Declaration documents signed for your hired talent
      </p>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <StatCard size="small">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#e6f4ff", color: "#1677ff" }}>
                <FileTextOutlined />
              </div>
              <div className="stat-info">
                <h3>{stats.total}</h3>
                <p>Total Hiring Records</p>
              </div>
            </div>
          </StatCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatCard size="small">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#f6ffed", color: "#52c41a" }}>
                <CheckCircleOutlined />
              </div>
              <div className="stat-info">
                <h3>{stats.allSigned}</h3>
                <p>Fully Signed</p>
              </div>
            </div>
          </StatCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatCard size="small">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#fff7e6", color: "#fa8c16" }}>
                <ClockCircleOutlined />
              </div>
              <div className="stat-info">
                <h3>{stats.pending}</h3>
                <p>Awaiting Signature</p>
              </div>
            </div>
          </StatCard>
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by hiring ID or talent name..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 320 }}
        />
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          {!loading && groupedRows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No compliance documents found. Documents are created automatically when a talent is hired."
              style={{ padding: "48px 0" }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={filteredRows}
              rowKey="hiringRecordId"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} hiring records`,
              }}
              scroll={{ x: 900 }}
              locale={{ emptyText: " " }}
            />
          )}
        </Spin>
      </Card>

      {/* Document viewer modal */}
      {viewDoc && (
        <ComplianceDocViewer
          mode="modal"
          document={viewDoc}
          open={true}
          onClose={() => setViewDoc(null)}
        />
      )}
    </PageWrapper>
  );
};

export default CustomerComplianceDocs;
